import { useState } from "react";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePoll from "@/hooks/usePoll";
import { useVerifier, VerifyInput, VerifyResult } from "@/components/PermitVerifier";
import { DeliveryBadge, Empty, ErrorBox, Field, Money, PageHeader } from "@/components/common";

function DeliverBox({ permit, onDone }) {
  const [tag, setTag] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/permits/${permit.id}/deliver`, { ear_tag_confirm: tag, notes: notes || null }); toast.success(`تم تسجيل تسليم الكبش ${permit.ear_tag}`); onDone(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (permit.delivered_at) return <div className="rounded-xl bg-blue-50 border border-blue-300 p-3 font-bold text-blue-800" data-testid="already-delivered">تم تسليم هذا الكبش مسبقًا</div>;
  return (
    <div className="rounded-xl border-2 border-[#1A4D2E]/30 p-4 space-y-3" data-testid="deliver-box">
      <Field label="أدخل الرقم الموجود على الكبش للمطابقة"><input className="k-input h-14 text-2xl num text-center" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="deliver-eartag-input" /></Field>
      <Field label="ملاحظات (اختياري)"><input className="k-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="deliver-notes-input" /></Field>
      <button className="k-btn-primary w-full h-14 text-lg" disabled={busy || !tag.trim()} onClick={go} data-testid="confirm-delivery-button">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageCheck className="w-5 h-5" />} تأكيد التسليم داخل الحظيرة</button>
      <ErrorBox text={err} />
    </div>
  );
}

function MySections() {
  const { data, error } = usePoll("/sections/mine", null, 20000);
  if (error) return <ErrorBox text={error} />;
  if (!data) return null;
  if (!data.sections.length) return <Empty text="لا توجد أقسام مسندة إليك" />;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        {data.sections.map((s) => (
          <div key={s.id} className="k-card p-4" data-testid={`my-section-${s.code}`}>
            <div className="flex justify-between"><b className="font-display text-lg">{s.code} – {s.name}</b><Money v={s.default_price} /></div>
            <div className="text-sm text-slate-500">{s.location} · مباع ومسجل: {data.sheep.filter((x) => x.section_id === s.id).length}</div>
          </div>
        ))}
      </div>
      <div className="k-card overflow-x-auto">
        <table className="k-table"><thead><tr><th>رقم الكبش</th><th>الزبون</th><th>القسم</th><th>العامل</th><th>الحالة</th></tr></thead>
          <tbody>{data.sheep.map((x) => <tr key={x.id}><td className="num text-lg">{x.ear_tag}</td><td>{x.customer_name}</td><td>{data.sections.find((s) => s.id === x.section_id)?.code}</td><td>{x.worker_name}</td><td><DeliveryBadge s={x.delivery_status} /></td></tr>)}</tbody>
        </table>
        {!data.sheep.length && <Empty text="لا توجد أغنام مسجلة كمباعة في أقسامك بعد" />}
      </div>
      <p className="text-xs text-slate-500">تظهر هنا الأغنام التي سجّلها المكتب فقط. الأغنام المختارة والمرقمة يدويًا ولم تُسجل بعد لا تظهر.</p>
    </div>
  );
}

export default function Barn() {
  const { can } = useAuth();
  const v = useVerifier();
  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader title="الحظيرة والتسليم" subtitle="تحقق من تصريح الخروج وطابق رقم الكبش قبل تسليم الكبش للزبون" />
      <Tabs defaultValue="verify" dir="rtl">
        <TabsList className="mb-4 h-12">
          <TabsTrigger value="verify" className="h-10 px-5 font-bold" data-testid="barn-tab-verify">التحقق والتسليم</TabsTrigger>
          {can("view_my_sections") && <TabsTrigger value="mine" className="h-10 px-5 font-bold" data-testid="barn-tab-mine">أقسامي</TabsTrigger>}
        </TabsList>
        <TabsContent value="verify" className="space-y-5">
          <VerifyInput v={v} />
          <VerifyResult res={v.res}>
            {v.res?.result === "valid" && can("confirm_delivery") && <DeliverBox permit={v.res.permit} onDone={() => v.verify(v.res.permit.number)} />}
          </VerifyResult>
        </TabsContent>
        <TabsContent value="mine"><MySections /></TabsContent>
      </Tabs>
    </div>
  );
}
